import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar';
import { X } from 'lucide-react';
import {
  FileType,
  SharedUser,
} from 'features/file-manager/utils/file-manager';
import { useIsMobile } from 'hooks/use-mobile';
import { CustomtDateFormat } from 'lib/custom-date-formatter';
import { useTheme } from 'components/core/theme-provider';
import { useDocumentSharedUsersQuery } from '../hooks/use-document-manager';
import { useIsMediaLibrary } from 'hooks/use-is-media-library';
import { getFileBlobUrl } from 'services/storage-service';
import { useConfig } from 'hooks/use-config';
import { useGetGroupsByIds } from 'features/iam/hooks/use-iam';
import pdfIcon from 'assets/images/pdf-icon.svg';
import documentIcon from 'assets/images/document-icon.svg';
import csvIcon from 'assets/images/csv.svg';
import jsonIcon from 'assets/images/json.svg';
import powerpointIcon from 'assets/images/pptx.svg';
import vsdxIcon from 'assets/images/vsdx.svg';
import xlsxIcon from 'assets/images/xlsx.svg';
import { cn } from 'lib/utils';

export interface IFileDataWithSharing {
  id: string;
  name: string;
  fileType: FileType;
  size: string;
  lastModified: string | Date;
  isShared: boolean;
  sharedWith?: SharedUser[];
  sharePermissions?: { [key: string]: string };
  ownerId?: string;
}

export interface BaseFileDetailsSheetProps {
  isOpen: boolean;
  onClose: () => void;
  file: IFileDataWithSharing | null;
  t: (key: string) => string;
  variant?: 'default' | 'trash';
}

interface BaseFile {
  id: string | number;
  name: string;
  fileType: string;
}

interface GetGroupsByIdsResponse {
  response: any;
}

const getCreationDate = (lastModified: Date | string): Date => {
  const creationDate = new Date(lastModified);
  const randomArray = new Uint32Array(1);
  crypto.getRandomValues(randomArray);
  const hoursBack = (randomArray[0] % 721) + 24;
  creationDate.setHours(creationDate.getHours() - hoursBack);
  return creationDate;
};

// const getOwnerName = (sharedUsers: SharedUser[], variant: 'default' | 'trash'): string => {
//   if (variant === 'trash') {
//     return 'Luca Meier';
//   }
//   return sharedUsers.find((user) => user.role === 'Owner')?.name ?? '';
// };

const getDateCreated = (file: IFileDataWithSharing, variant: 'default' | 'trash'): Date => {
  if (variant === 'trash' && file.lastModified instanceof Date) {
    return getCreationDate(file.lastModified);
  }
  return new Date(file.lastModified);
};

const FallbackAvatar: React.FC<{ className?: string; children: React.ReactNode }> = ({
  className = '',
  children,
}) => <div className={`rounded-full overflow-hidden ${className}`}>{children}</div>;

const FallbackAvatarImage: React.FC<{ src?: string; alt: string }> = ({ src, alt }) => {
  if (!src) return null;
  return <img src={src} alt={alt} className="w-full h-full object-cover" />;
};

const FallbackAvatarFallback: React.FC<{ className?: string; children: React.ReactNode }> = ({
  className = '',
  children,
}) => (
  <div className={`w-full h-full flex items-center justify-center ${className}`}>{children}</div>
);

// Main shared component
const FileDetailsSheet: React.FC<BaseFileDetailsSheetProps> = ({
  isOpen,
  onClose,
  file,
  t,
  variant = 'default',
}) => {
  const isMobile = useIsMobile();
  const { theme } = useTheme();
  const isMediaLibrary = useIsMediaLibrary();
  const [fileUrl, setFileUrl] = useState<string | undefined>(undefined);
  const { configurationSettings } = useConfig();
  const [loaded, setLoaded] = useState(false);
  console.log('loaded', loaded)

  const isFetchingRef = useRef(false);

  useEffect(() => {
    setFileUrl(undefined);
    async function getFileUrlAsync() {
      if (file?.fileType !== 'Photo') return;
      if (isFetchingRef.current) return;

      isFetchingRef.current = true;
      try {
        if (!file) return;
        const url = await getFileUrl(file as BaseFile);
        setFileUrl(url);
      } catch (error) {
        setFileUrl(undefined);
      } finally {
        isFetchingRef.current = false;
      }
    }
    getFileUrlAsync();
  }, [file?.id, file?.name, file?.fileType]);

  const allUserIds = useMemo(() => {
    if (!file) return [];
    const sharedIds = file.sharedWith?.map((u) => u.id) ?? [];
    return file.ownerId ? [...sharedIds, file.ownerId] : sharedIds;
  }, [file?.id, file?.ownerId]);

  const allSharedGroupsIds = useMemo(() => {
    if (!file) return [];
    const groupIds = file.sharedWith?.filter((u) => u.type === 'USER_GROUP').map((g) => g.id) ?? [];
    return groupIds;
  }, [file?.id]);

  const { data: users, error } = useDocumentSharedUsersQuery({
    page: 0,
    pageSize: 10,
    filter: {
      userIds: allUserIds,
    },
    enabled: isOpen && !!file && !!allUserIds.length,
  });

  const { data: currentSharedGroups } = useGetGroupsByIds({ groupIds: allSharedGroupsIds }) as { data: GetGroupsByIdsResponse };
  const currentSharedGroupsResponse = currentSharedGroups?.response || [];

  const ownerUser = React.useMemo(() => {
    if (!file?.ownerId || !users || allUserIds.length === 0) return null;
    return users.find((u) => u.id === file.ownerId) ?? null;
  }, [users, file?.ownerId, allUserIds.length]);

  const sharedUsers = React.useMemo(() => {
    if (!users || allUserIds.length === 0) return [];
    return users.filter((u) => u.id !== file?.ownerId);
  }, [users, file?.ownerId, allUserIds.length]);

  if (error) {
    return <div className="p-4 text-error">{t('ERROR_LOADING_SHARED_USERS')}</div>;
  }

  if (!isOpen || !file) return null;

  // const IconComponent = getFileTypeIcon(file.fileType);
  // const { iconColor } = getFileTypeInfo(file.fileType);

  // const ownerName = getOwnerName(sharedUsers, variant);
  const creationDate = getDateCreated(file, variant);
  // const fileTypeDisplayName = getFileTypeDisplayName(file.fileType);

  const containerClasses = isMobile
    ? 'absolute inset-y-0 inset-x-4 z-50 flex flex-col overflow-hidden rounded-xl border border-[#e7e7e7] bg-card shadow-xl dark:border-grey-400'
    : 'absolute top-0 right-0 z-50 flex h-full w-[45vh] flex-col overflow-hidden rounded-xl border border-[#e7e7e7] bg-card dark:border-grey-400';

  const headerClasses = isMobile
    ? 'shrink-0 border-b border-[#e7e7e7] bg-card p-4 pb-3 dark:border-grey-400'
    : 'border-b border-[#e7e7e7] p-6 pb-2 dark:border-grey-400';

  const contentClasses = isMobile
    ? 'flex-1 overflow-y-auto p-4 space-y-6'
    : 'flex-1 overflow-y-auto pt-4 px-6 pb-6 space-y-6';

  const AvatarComponent = Avatar || FallbackAvatar;
  const AvatarImageComponent = AvatarImage || FallbackAvatarImage;
  const AvatarFallbackComponent = AvatarFallback || FallbackAvatarFallback;



  async function getFileUrl<T extends BaseFile>(file: T): Promise<string | undefined> {
    return getFileBlobUrl(file.id.toString(), configurationSettings, undefined);
  }

  const fileTypeIconMap: Record<string, string> = {
    Document: documentIcon,
    Pdf: pdfIcon,
    Xlsx: xlsxIcon,
    Pptx: powerpointIcon,
    Json: jsonIcon,
    Csv: csvIcon,
    Vsdx: vsdxIcon,
  };

  const filesImageViewer = () => {
    const fileIcon = fileTypeIconMap[file?.fileType] ?? documentIcon;
    return <div className="w-full rounded-lg flex items-center justify-center">
      <img
        src={fileIcon}
        alt={file.name}
        className={`w-full h-[200px] rounded-[8px] transition-opacity duration-300`}
      />
    </div>;
  };

  return (
    <div className={cn(containerClasses, 'transition-opacity duration-300')}>
      <div className={headerClasses}>
        <div className="flex items-center justify-between">
          <h2 className={theme === 'dark' ? 'text-lg font-semibold text-gray-50' : 'text-lg font-semibold text-gray-900'}>{t('DETAILS')}</h2>
          <button
            onClick={onClose}
            className={`w-5 h-5 relative overflow-hidden flex items-center justify-center group transition-opacity hover:opacity-80 ml-3 ${theme === 'dark' ? 'border-gray-50' : 'border-gray-600'} border-2 rounded-full`}
          >
            <X className={`w-[12px] h-[12px] ${theme === 'dark' ? 'text-gray-50' : 'text-gray-600'}`} strokeWidth={4} />
            <span className="sr-only">Close </span>
          </button>
        </div>
      </div>

      <div className={contentClasses}>
        <div className="flex flex-col items-center space-y-3">
          <div
            className={`h-[200px] w-full rounded-lg flex items-center justify-center relative`}
          >
            {file.fileType === 'Photo' ? (
              <>
                {!loaded && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-card rounded-[8px] border border-[#e7e7e7] dark:border-grey-400">
                    <svg xmlns="http://www.w3.org/2000/svg" width="48" opacity="0.9" color='#dc3848' height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-image-icon lucide-image">
                      <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
                      <circle cx="9" cy="9" r="2" />
                      <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
                    </svg>
                  </div>
                )}

                {fileUrl && (
                  <div className="h-full w-full rounded-[8px] overflow-hidden relative border border-[#e7e7e7] dark:border-grey-400">
                    <img
                      src={fileUrl}
                      alt={file.name}
                      onLoad={() => setLoaded(true)}
                      className={`object-cover w-full h-full rounded-[8px] transition-opacity duration-300`}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className={`${contentClasses} relative flex justify-center items-center`}>
                {filesImageViewer()}
              </div>
            )}

          </div>
          {/* <h3 className={theme === 'dark' ? 'text-base text-gray text-center break-all' : 'text-base text-gray-900 text-center break-all'}>{file.name}</h3> */}
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-[120px_1fr] gap-y-3">
            <span className="text-muted-foreground text-sm">{t('FILE_NAME')}</span>
            <span className="truncate text-[16px]">{file.name || '-'}</span>
            <span className="text-muted-foreground text-sm">{t('SIZE')}</span>
            <span className="text-[16px]">{file.size || '-'}</span>
            <span className="text-muted-foreground text-sm">{t('DATE_CREATED')}</span>
            <span className="text-[16px]">
              {CustomtDateFormat(creationDate, { dateDelimiter: '.' }) || '-'}
            </span>
          </div>

          {ownerUser && <hr className="h-[1px] bg-gray-400 border-0 rounded mt-6" />}

          <div className="space-y-4">

            {ownerUser && (
              <div className="grid grid-cols-[120px_1fr] gap-y-3">
                <span className={theme === 'dark' ? 'text-sm text-gray text-sm' : 'text-sm text-gray-600'}>{t('OWNER')}</span>
                <span className={theme === 'dark' ? 'text-sm font-medium text-gray  text-[16px]' : 'text-sm font-medium text-gray-900'}>{ownerUser.name}</span>
              </div>
            )}

            {/* <div className="space-y-1">
              <label className="text-sm text-gray-600">{t('LAST_MODIFIED')}</label>
              <div className="text-sm font-medium text-gray-900">
                {CustomtDateFormat(file.lastModified)}
              </div>
            </div> */}

            {/* {file.fileType !== 'Folder' && (
              <div className="space-y-1">
                <label className="text-sm text-gray-600">{t('LOCATION')}</label>
                <div className="text-sm font-medium text-gray-900">/Documents/</div>
              </div>
            )}

            <div className="space-y-1">
              <label className="text-sm text-gray-600">{t('SHARING')}</label>
              <div className="text-sm font-medium text-gray-900">
                {file.isShared ? (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                    {t('SHARED')}
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
                    {t('PRIVATE')}
                  </span>
                )}
              </div>
            </div> */}
          </div>
        </div>

        {sharedUsers.length > 0 && !isMediaLibrary && <hr className="h-[1px] bg-gray-400 border-0 rounded mt-6" />}
        {sharedUsers.length > 0 && !isMediaLibrary && (
          <div className="space-y-4">
            <h3 className={theme === 'dark' ? 'text-base text-sm font-medium text-gray' : 'text-sm text-base font-medium text-gray-900'}>
              {t('SHARED_WITH')} {sharedUsers.length > 1 ? t('USERS') : t('USER')} ({sharedUsers.length})
            </h3>

            <div className="space-y-3">
              {sharedUsers.map((user) => (
                <div key={user.id} className="flex items-center gap-3">
                  <AvatarComponent className="h-9 w-9">
                    <AvatarImageComponent src={user?.avatar} alt={user.name} />
                    <AvatarFallbackComponent className={theme === 'dark' ? 'text-xs bg-gray-500' : 'text-xs bg-gray-100'}>
                      {(user?.name && user.name.trim() !== '' ? user.name : user.id)
                        .trim()[0]
                        .toUpperCase()}
                    </AvatarFallbackComponent>
                  </AvatarComponent>
                  <div className="flex-1 min-w-0">
                    <div className={theme === 'dark' ? 'text-sm font-medium text-gray truncate' : 'text-sm font-medium text-gray-900 truncate'}>
                      {user?.name && user?.name.trim() !== '' ? user?.name : user?.email || user?.id}
                      {/* {user?.role === 'Owner' && (
                        <span className="text-gray-500 font-normal"> (you)</span>
                      )} */}
                    </div>
                    {/* <div className="text-xs text-gray-500 capitalize">
                      {(user.role ?? '').toLowerCase()}
                    </div> */}
                  </div>
                  {/* {user.role !== 'Owner' && <div className="text-xs text-gray-400"></div>} */}
                </div>
              ))}
            </div>
          </div>
        )}

        {currentSharedGroupsResponse.length > 0 && !isMediaLibrary && <hr className="h-[1px] bg-gray-400 border-0 rounded mt-6" />}
        {currentSharedGroupsResponse.length > 0 && !isMediaLibrary && (
          <div className="space-y-4">
            <h3 className={theme === 'dark' ? 'text-base font-medium text-sm text-gray' : 'text-sm text-base font-medium text-gray-900'}>
              {t('SHARED_WITH')} {currentSharedGroupsResponse.length > 1 ? t('GROUPS') : t('GROUP')} ({currentSharedGroupsResponse.length})
            </h3>

            <div className="space-y-3">
              {currentSharedGroupsResponse.map((group: any) => (
                <div key={group.id} className="flex items-center gap-3">
                  <AvatarComponent className="h-9 w-9">
                    <AvatarImageComponent src={group?.avatar} alt={group.groupName} />
                    <AvatarFallbackComponent className={theme === 'dark' ? 'text-xs bg-gray-500' : 'text-xs bg-gray-100'}>
                      {(group?.groupName && group.groupName.trim() !== '' ? group.groupName : group.id)
                        .trim()[0]
                        .toUpperCase()}
                    </AvatarFallbackComponent>
                  </AvatarComponent>
                  <div className="flex-1 min-w-0">
                    <div className={theme === 'dark' ? 'text-sm font-medium text-gray truncate' : 'text-sm font-medium text-gray-900 truncate'}>
                      {group?.groupName && group?.groupName.trim() !== '' ? group?.groupName : group?.email || group?.id}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileDetailsSheet;
